/**
 * Time parsing + active/upcoming/ended status.
 *
 * Feed timestamps are ISO with an IST offset, e.g. "2026-07-24T23:59:00+05:30"
 * (older exports may look like "2026-06-18 00:00:00.0"). We parse the date
 * portion only — status is computed against a configurable `now` so a
 * historical export can be scrubbed through time.
 */

/** Parse "YYYY-MM-DD ..." (or ISO) into a Date at local midnight; null if unparseable. */
export function parseIst(value) {
  if (!value) return null;
  const m = String(value).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/** "active" | "upcoming" | "ended" | "unknown" relative to `now` (a Date). */
export function statusOf(event, now) {
  const start = parseIst(event.event_start ?? event.event_start_ist);
  const end = parseIst(event.event_end ?? event.event_end_ist);
  if (!start && !end) return "unknown";
  const ref = now instanceof Date ? now : new Date();
  if (end && end < startOfDay(ref)) return "ended";
  if (start && start > endOfDay(ref)) return "upcoming";
  return "active";
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function endOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "18 Jun" — compact date for the "When" row. */
export function fmtDate(value) {
  const d = parseIst(value);
  if (!d) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "18 Jun → 21 Jun" */
export function fmtRange(startVal, endVal) {
  return `${fmtDate(startVal)} → ${fmtDate(endVal)}`;
}

export const STATUS_META = {
  active: { label: "Active", color: "#1A7F37", bg: "#E6F4EA" },
  upcoming: { label: "Upcoming", color: "#9A6700", bg: "#FFF8E1" },
  ended: { label: "Ended", color: "#6E7781", bg: "#F0F1F3" },
  unknown: { label: "Undated", color: "#6E7781", bg: "#F0F1F3" },
};

/** Day-granular epoch helpers for the range slider. */
export function dayEpoch(value) {
  const d = parseIst(value);
  return d ? d.getTime() : null;
}
